import {useEffect, useState} from 'react';

type OAuthSession = {
  accessToken: string;
  refreshToken: string;
  scope: string;
  expiresAt: string;
};

type Result = {
  kind: 'idle' | 'success' | 'error';
  message: string;
  data?: unknown;
};

type TokenResponse = {
  access_token?: unknown;
  refresh_token?: unknown;
  scope?: unknown;
  expires_in?: unknown;
  error?: unknown;
  error_description?: unknown;
};

const storageKeys = {
  session: 'biqli-pkce-session',
  state: 'biqli-pkce-state',
  verifier: 'biqli-pkce-verifier',
  expectedFailure: 'biqli-pkce-expected-failure',
};

const baseUrl = (import.meta.env.VITE_BIQLI_OAUTH_BASE_URL?.trim() || 'https://biq.li').replace(/\/$/, '');
const clientId = import.meta.env.VITE_BIQLI_OAUTH_CLIENT_ID?.trim() || '';
const redirectUri = import.meta.env.VITE_BIQLI_OAUTH_REDIRECT_URI?.trim() || `${window.location.origin}/`;
const requestedScope = import.meta.env.VITE_BIQLI_OAUTH_SCOPE?.trim() || 'workspace.read links.view';

export default function PublicOAuthSmoke() {
  const [session, setSession] = useState<OAuthSession | null>(() => readSession());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result>({
    kind: 'idle',
    message: 'Ready to test a public OAuth client with S256 PKCE.',
  });

  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search);
    if (!parameters.has('code') && !parameters.has('error')) return;

    const callbackParameters = new URLSearchParams(parameters);
    removeOAuthCallbackParameters(parameters);
    void completeAuthorization(callbackParameters);
  }, []);

  async function completeAuthorization(parameters: URLSearchParams) {
    setBusy(true);
    try {
      const returnedState = parameters.get('state');
      const expectedState = sessionStorage.getItem(storageKeys.state);
      const verifier = sessionStorage.getItem(storageKeys.verifier);
      const expectedFailure = sessionStorage.getItem(storageKeys.expectedFailure);
      clearHandshake();
      if (!returnedState || !expectedState || returnedState !== expectedState) {
        throw new Error('The OAuth callback state was missing or invalid.');
      }

      const error = parameters.get('error');
      if (error) {
        throw new Error(parameters.get('error_description') || error);
      }

      const code = parameters.get('code');
      if (!code || !verifier) {
        throw new Error('The OAuth authorization code or PKCE verifier was missing.');
      }

      let nextSession: OAuthSession;
      try {
        nextSession = await exchangeToken({
          grant_type: 'authorization_code',
          client_id: clientId,
          code,
          redirect_uri: redirectUri,
          code_verifier: verifier,
        });
      } catch (error) {
        if (
          expectedFailure === 'invalid_verifier'
          && error instanceof OAuthRequestError
          && error.status === 400
          && error.code === 'invalid_grant'
        ) {
          clearSession();
          setSession(null);
          setResult({
            kind: 'success',
            message: 'PKCE rejected the incorrect code verifier.',
            data: {status: error.status, error: error.code},
          });

          return;
        }

        throw error;
      }
      if (expectedFailure === 'invalid_verifier') {
        storeSession(nextSession);
        setSession(nextSession);
        setResult({
          kind: 'error',
          message: 'The provider accepted an incorrect PKCE code verifier.',
          data: {status: 200},
        });

        return;
      }
      storeSession(nextSession);
      setSession(nextSession);
      const identity = await fetchIdentity(nextSession.accessToken);
      setResult({
        kind: 'success',
        message: 'PKCE authorization and /oauth/me passed.',
        data: identity,
      });
    } catch (error) {
      clearSession();
      setSession(null);
      setResult(failureResult(error));
    } finally {
      setBusy(false);
    }
  }

  async function connect(useInvalidVerifier = false) {
    setBusy(true);
    try {
      if (!clientId) throw new Error('Set VITE_BIQLI_OAUTH_CLIENT_ID first.');

      const verifier = randomBase64Url(64);
      const state = randomBase64Url(32);
      const challenge = await sha256Base64Url(verifier);
      sessionStorage.setItem(
        storageKeys.verifier,
        useInvalidVerifier ? randomBase64Url(64) : verifier,
      );
      sessionStorage.setItem(storageKeys.state, state);
      if (useInvalidVerifier) {
        sessionStorage.setItem(storageKeys.expectedFailure, 'invalid_verifier');
      } else {
        sessionStorage.removeItem(storageKeys.expectedFailure);
      }

      const authorizationUrl = new URL('/oauth/authorize', baseUrl);
      authorizationUrl.search = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: requestedScope,
        state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
      }).toString();
      window.location.assign(authorizationUrl);
    } catch (error) {
      clearHandshake();
      setResult(failureResult(error));
      setBusy(false);
    }
  }

  async function verifyIdentity() {
    if (!session) return;
    await run('Public-client /oauth/me passed.', () => fetchIdentity(session.accessToken));
  }

  async function refresh() {
    if (!session) return;
    await run('Public-client refresh and /oauth/me passed.', async () => {
      const nextSession = await exchangeToken({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: session.refreshToken,
      });
      storeSession(nextSession);
      setSession(nextSession);

      return fetchIdentity(nextSession.accessToken);
    });
  }

  async function revoke() {
    if (!session) return;
    setBusy(true);
    try {
      const response = await fetch(`${baseUrl}/api/v1/oauth/revoke`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: clientId,
          token: session.refreshToken,
        }),
      });
      if (!response.ok) throw await responseError(response, 'OAuth revocation failed');

      const verification = await fetch(`${baseUrl}/api/v1/oauth/me`, {
        headers: {Accept: 'application/json', Authorization: `Bearer ${session.accessToken}`},
      });
      clearSession();
      setSession(null);
      setResult({
        kind: verification.status === 401 ? 'success' : 'error',
        message: verification.status === 401
          ? 'Public-client revocation rejected the old access token.'
          : `Revocation verification returned HTTP ${verification.status}.`,
        data: {active_token_status: verification.status},
      });
    } catch (error) {
      setResult(failureResult(error));
    } finally {
      setBusy(false);
    }
  }

  async function run(message: string, operation: () => Promise<unknown>) {
    setBusy(true);
    try {
      setResult({kind: 'success', message, data: await operation()});
    } catch (error) {
      setResult(failureResult(error));
    } finally {
      setBusy(false);
    }
  }

  function disconnect() {
    clearHandshake();
    clearSession();
    setSession(null);
    setResult({kind: 'idle', message: 'Local public-client session cleared.'});
  }

  return (
    <section className="card">
      <p className="eyebrow">External public client</p>
      <h1>Biqli OAuth PKCE smoke test</h1>
      <p className="description">
        The browser uses authorization code flow with S256 PKCE. No client secret is configured or sent.
      </p>

      <dl>
        <div>
          <dt>Configuration</dt>
          <dd>{clientId ? 'Ready' : 'Missing OAuth client ID'}</dd>
        </div>
        <div>
          <dt>Connection</dt>
          <dd>{session ? 'Connected' : 'Not connected'}</dd>
        </div>
        <div>
          <dt>Callback</dt>
          <dd>{redirectUri}</dd>
        </div>
        <div>
          <dt>Granted/requested scopes</dt>
          <dd>{session?.scope || requestedScope}</dd>
        </div>
        <div>
          <dt>Access token expires</dt>
          <dd>{session?.expiresAt || 'Not connected'}</dd>
        </div>
      </dl>

      <div className="actions oauth-actions">
        <button disabled={busy || !clientId || Boolean(session)} onClick={() => connect()}>
          Connect with PKCE
        </button>
        <button disabled={busy || !clientId || Boolean(session)} onClick={() => connect(true)}>
          Reject bad verifier
        </button>
        <button disabled={busy || !session} onClick={verifyIdentity}>
          Get connected workspace
        </button>
        <button disabled={busy || !session} onClick={refresh}>
          Refresh and verify
        </button>
        <button disabled={busy || !session} onClick={revoke}>
          Revoke and verify
        </button>
        <button disabled={busy} onClick={disconnect}>
          Clear local session
        </button>
      </div>

      <output className={result.kind}>
        <strong>{result.message}</strong>
        {result.data !== undefined && <pre>{JSON.stringify(result.data, null, 2)}</pre>}
      </output>
    </section>
  );
}

async function exchangeToken(parameters: Record<string, string>): Promise<OAuthSession> {
  const response = await fetch(`${baseUrl}/api/v1/oauth/token`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(parameters),
  });
  const payload = await response.json() as TokenResponse;
  if (!response.ok) {
    const description = typeof payload.error_description === 'string'
      ? payload.error_description
      : `Token exchange failed with HTTP ${response.status}.`;
    throw new OAuthRequestError(
      description,
      response.status,
      typeof payload.error === 'string' ? payload.error : 'unknown_error',
    );
  }
  if (typeof payload.access_token !== 'string' || typeof payload.refresh_token !== 'string') {
    throw new Error('The token response was missing required fields.');
  }

  const expiresIn = typeof payload.expires_in === 'number' ? Math.max(1, payload.expires_in) : 3600;
  return {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    scope: typeof payload.scope === 'string' ? payload.scope : '',
    expiresAt: new Date(Date.now() + expiresIn * 1000).toISOString(),
  };
}

async function fetchIdentity(accessToken: string) {
  const response = await fetch(`${baseUrl}/api/v1/oauth/me`, {
    headers: {Accept: 'application/json', Authorization: `Bearer ${accessToken}`},
  });
  if (!response.ok) throw await responseError(response, 'Retrieving the connected workspace failed');

  return response.json();
}

async function responseError(response: Response, fallback: string) {
  const payload = await response.json().catch(() => null) as {
    error?: string;
    error_description?: string;
    message?: string;
  } | null;
  return new Error(payload?.error_description || payload?.message || payload?.error || `${fallback} with HTTP ${response.status}.`);
}

function failureResult(error: unknown): Result {
  return {
    kind: 'error',
    message: error instanceof Error ? error.message : 'Unknown OAuth error.',
  };
}

function readSession(): OAuthSession | null {
  try {
    const value = sessionStorage.getItem(storageKeys.session);
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<OAuthSession>;
    return typeof parsed.accessToken === 'string'
      && typeof parsed.refreshToken === 'string'
      && typeof parsed.scope === 'string'
      && typeof parsed.expiresAt === 'string'
      ? parsed as OAuthSession
      : null;
  } catch {
    return null;
  }
}

function storeSession(session: OAuthSession) {
  sessionStorage.setItem(storageKeys.session, JSON.stringify(session));
}

function clearSession() {
  sessionStorage.removeItem(storageKeys.session);
}

function clearHandshake() {
  sessionStorage.removeItem(storageKeys.state);
  sessionStorage.removeItem(storageKeys.verifier);
  sessionStorage.removeItem(storageKeys.expectedFailure);
}

function removeOAuthCallbackParameters(parameters: URLSearchParams) {
  for (const key of ['code', 'state', 'error', 'error_description']) parameters.delete(key);
  const query = parameters.toString();
  window.history.replaceState({}, document.title, `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
}

function randomBase64Url(byteLength: number) {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
  return bytesToBase64Url(bytes);
}

async function sha256Base64Url(value: string) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return bytesToBase64Url(new Uint8Array(digest));
}

function bytesToBase64Url(bytes: Uint8Array) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

class OAuthRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'OAuthRequestError';
  }
}
